import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  RefreshCw, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  Settings,
  Zap,
  Database
} from "lucide-react";
import { API } from "./constants";
import { useAuth } from "@/contexts/AuthContext";

// Translations
const TEXTS = {
  en: {
    title: "Data Sync",
    lastSync: "Last sync",
    nextSync: "Next sync",
    pendingFirst: "Pending first run",
    syncNow: "Sync Now",
    syncing: "Syncing...",
    autoSync: "Auto Sync",
    schedule: "Daily at 00:00 CST",
    postsEnriched: "posts synced",
    platforms: "Platforms",
    success: "Sync completed",
    error: "Sync failed",
    settings: "Settings",
    enabled: "Enabled",
    disabled: "Disabled",
  },
  ko: {
    title: "데이터 동기화",
    lastSync: "마지막 동기화",
    nextSync: "다음 동기화",
    pendingFirst: "첫 실행 대기 중",
    syncNow: "지금 동기화",
    syncing: "동기화 중...",
    autoSync: "자동 동기화",
    schedule: "매일 00:00 (중국 시간)",
    postsEnriched: "개 포스트 동기화됨",
    platforms: "플랫폼",
    success: "동기화 완료",
    error: "동기화 실패",
    settings: "설정",
    enabled: "활성화",
    disabled: "비활성화",
  },
  zh: {
    title: "数据同步",
    lastSync: "上次同步",
    nextSync: "下次同步",
    pendingFirst: "等待首次运行",
    syncNow: "立即同步",
    syncing: "同步中...",
    autoSync: "自动同步",
    schedule: "每天00:00 (中国时间)",
    postsEnriched: "帖子已同步",
    platforms: "平台",
    success: "同步完成",
    error: "同步失败",
    settings: "设置",
    enabled: "已启用",
    disabled: "已禁用",
  },
};

// Format relative time
const formatRelativeTime = (isoString, lang) => {
  if (!isoString || isoString === "Pending first run") {
    return TEXTS[lang]?.pendingFirst || isoString;
  }
  
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    
    if (diffMs < 0) {
      // Past
      const absMins = Math.abs(diffMins);
      const absHours = Math.abs(diffHours);
      
      if (absMins < 60) {
        return lang === "ko" ? `${absMins}분 전` : lang === "zh" ? `${absMins}分钟前` : `${absMins}m ago`;
      }
      if (absHours < 24) {
        return lang === "ko" ? `${absHours}시간 전` : lang === "zh" ? `${absHours}小时前` : `${absHours}h ago`;
      }
      return date.toLocaleDateString();
    } else {
      // Future
      if (diffMins < 60) {
        return lang === "ko" ? `${diffMins}분 후` : lang === "zh" ? `${diffMins}分钟后` : `in ${diffMins}m`;
      }
      if (diffHours < 24) {
        return lang === "ko" ? `${diffHours}시간 후` : lang === "zh" ? `${diffHours}小时后` : `in ${diffHours}h`;
      }
      return date.toLocaleDateString();
    }
  } catch {
    return isoString;
  }
};

export const DataEnrichmentStatus = ({ lang = "en" }) => {
  const t = TEXTS[lang] || TEXTS.en;
  const { isAdmin } = useAuth();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Fetch status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/scheduler/enrich-status`);
      setStatus(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchStatus();
    // Refresh every 60 seconds
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);
  
  // Trigger manual sync
  const handleTriggerSync = async () => {
    setSyncing(true);
    setError(null);
    
    try {
      const res = await axios.post(`${API}/scheduler/enrich-trigger`);
      if (res.data?.status === "started" || res.data?.status === "already_running") {
        // Background task started - poll for completion
        const pollStatus = async (retries = 0) => {
          if (retries > 30) {
            setSyncing(false);
            return;
          }
          await new Promise(r => setTimeout(r, 10000));
          try {
            const statusRes = await axios.get(`${API}/scheduler/enrich-status`);
            const lastResults = statusRes.data?.last_results;
            if (lastResults?.completed_at) {
              setShowSuccess(true);
              setTimeout(() => setShowSuccess(false), 5000);
              setSyncing(false);
              await fetchStatus();
              return;
            }
          } catch {}
          pollStatus(retries + 1);
        };
        pollStatus(0);
      } else {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        setSyncing(false);
        await fetchStatus();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setSyncing(false);
    }
  };
  
  // Toggle auto sync
  const handleToggleAutoSync = async (enabled) => {
    try {
      await axios.post(`${API}/scheduler/enrich-config?enabled=${enabled}`);
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-card border-border/50">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const lastResults = status?.last_results || {};
  const totalEnriched = lastResults.total_enriched || 0;
  const platforms = lastResults.platforms || {};
  
  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-blue-400" />
            <CardTitle className="text-sm font-medium">{t.title}</CardTitle>
            {status?.enabled ? (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                {t.enabled}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/30 text-xs">
                {t.disabled}
              </Badge>
            )}
          </div>
          
          {/* Settings Popover - Admin only */}
          {isAdmin && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid="sync-settings-btn">
                <Settings className="h-4 w-4 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t.autoSync}</span>
                  <Switch
                    checked={status?.enabled}
                    onCheckedChange={handleToggleAutoSync}
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  <Clock className="h-3 w-3 inline mr-1" />
                  {t.schedule}
                </div>
              </div>
            </PopoverContent>
          </Popover>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="py-3 px-4 pt-0">
        <div className="space-y-3">
          {/* Status Row */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-muted-foreground">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 cursor-help">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{t.lastSync}:</span>
                      <span className="text-foreground font-medium">
                        {formatRelativeTime(status?.last_enrich, lang)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {status?.last_enrich ? new Date(status.last_enrich).toLocaleString() : t.pendingFirst}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {status?.enabled && (
                <div className="flex items-center gap-1.5 text-xs">
                  <span>{t.nextSync}:</span>
                  <span className="text-blue-400">
                    {formatRelativeTime(status?.next_scheduled, lang)}
                  </span>
                </div>
              )}
            </div>
            
            {/* Sync Button */}
            {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={handleTriggerSync}
              disabled={syncing}
              data-testid="sync-now-btn"
            >
              {syncing ? (
                <>
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  {t.syncing}
                </>
              ) : (
                <>
                  <Zap className="h-3 w-3" />
                  {t.syncNow}
                </>
              )}
            </Button>
            )}
          </div>
          
          {/* Success/Error Messages */}
          {showSuccess && (
            <div className="flex items-center gap-2 text-emerald-400 text-xs bg-emerald-500/10 px-2 py-1.5 rounded">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>{t.success}: {totalEnriched} {t.postsEnriched}</span>
            </div>
          )}
          
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 px-2 py-1.5 rounded">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>{t.error}: {error}</span>
            </div>
          )}
          
          {/* Last Sync Results */}
          {totalEnriched > 0 && Object.keys(platforms).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-muted-foreground">{t.platforms}:</span>
              {Object.entries(platforms).map(([platform, data]) => (
                <Badge 
                  key={platform} 
                  variant="outline" 
                  className="bg-muted/30 text-foreground/80 border-border/50 text-xs font-normal"
                >
                  {platform}: {data?.enriched || 0}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DataEnrichmentStatus;
